import { useContext } from 'react'
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from '../../../../providers/notification';
  
interface Qualification {
    id: number;
    QualificationId: number;
    QualificationName: string;
    Score: number;
    CreatedBy: string;
    ModifiedBy: string;
    ActiveStatus: boolean;
    IsApprovalRequired: boolean;
    LevelBy: number;
    ApprovalTypeId: number;
    IsDeleted: boolean;
}

interface ComponentProps {
    handleClose: () => void;
    qualification: Qualification | undefined;
}

const CreateQualificationContent: React.FC<ComponentProps> = ({ handleClose, qualification }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const postQualification = (data: Qualification) => {
        return apiCall().post('/SetUp/CreateQualification', data);
    };

    const postQualificationMutation = useMutation(['post-qualification'], postQualification, {retry: 0});

    const postQualificationHandler = async (values: FormikValues, resetForm: () => void) => {
        const qualificationData: Qualification = {
            id: 0,
            QualificationId: 0,
            QualificationName: values?.name,
            Score: 0,
            CreatedBy: '',
            ModifiedBy: '',
            ActiveStatus: true,
            IsApprovalRequired: true,
            LevelBy: !qualification?.LevelBy ? 0 : ++qualification.LevelBy,
            ApprovalTypeId: 0,
            IsDeleted: false,
        }

        try {
            await postQualificationMutation.mutateAsync(qualificationData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Qualification has been successfully added',
                        type: "success",
                    });
                    queryClient.refetchQueries(['get-qualifications']);
                    handleClose();
                }
            })
        }catch(error: any) {
            showNotification({
                message: error?.response?.data?.Message || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: ''
        },
        onSubmit: (values, { resetForm }) => {
            postQualificationHandler(values, resetForm);
        },
        validationSchema,
    })

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postQualificationMutation.isLoading ? 'Submitting' : 'Submit'}
                            disabled={postQualificationMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateQualificationContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

